import { ProductThumbnailImage } from "@/components/product/ProductThumbnailImage";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import { handleActionError } from "@/lib/errors";
import {
  parseReorderLevel,
  stockItemTitle,
  stockItemVariantLabel
} from "@/lib/stock-item";
import {
  useBulkTrackStock,
  useStockCandidates
} from "@/queries/customer/stock-tracking";
import { toast } from "@medusajs/ui";
import { TruncateTooltip } from "@repo/design-system";
import { ImageOff, PackageSearch } from "lucide-react";
import { memo, useEffect, useState } from "react";

interface TrackFromOrdersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The cold-start seed (#71): pick purchasable variants from the salon's own
 * order history (`/candidates`, already de-duped against what they track) and
 * bulk-track them at a shared starting reorder level. An empty list here is the
 * main adoption risk, so this is the primary way the "My Stock" list gets
 * populated. Candidates are fetched lazily (only while the dialog is open).
 */
export const TrackFromOrdersDialog = memo(function TrackFromOrdersDialog({
  open,
  onOpenChange
}: TrackFromOrdersDialogProps) {
  const { data, isLoading, error } = useStockCandidates(undefined, {
    enabled: open
  });
  useReportLoadFailure(error, "track from orders dialog");
  const bulk = useBulkTrackStock();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [level, setLevel] = useState("1");

  // Fresh selection + default level every time the dialog opens.
  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setLevel("1");
    }
  }, [open]);

  const candidates = data?.candidates ?? [];
  const total = data?.count ?? candidates.length;
  const truncated = total > candidates.length;

  const allSelected =
    candidates.length > 0 && selected.size === candidates.length;

  const toggle = (variantId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(variantId)) {
        next.delete(variantId);
      } else {
        next.add(variantId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(
      allSelected ? new Set() : new Set(candidates.map((c) => c.variant_id))
    );
  };

  // Blank/invalid seeds at the default level of 1; `0` ("flag only when out")
  // is a valid choice and is kept, not bumped. Same rule as the row editor.
  const reorderLevel = parseReorderLevel(level, 1);

  const handleSubmit = () => {
    if (selected.size === 0 || bulk.isPending) {
      return;
    }
    const items = [...selected].map((variant_id) => ({
      variant_id,
      reorder_level: reorderLevel
    }));
    bulk.mutate(items, {
      onSuccess: (res) => {
        toast.success(
          `Now tracking ${res.count} ${res.count === 1 ? "item" : "items"}`
        );
        onOpenChange(false);
      },
      onError: (err) =>
        toast.error(
          handleActionError(err, {
            boundary: "track from orders",
            fallback:
              "We couldn't start tracking these products. Please try again."
          })
        )
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Track from your orders</DialogTitle>
          <DialogDescription>
            Pick the products you want to keep an eye on. We&apos;ll flag them
            when your on-hand count runs low.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-[200px] min-w-0 py-2">
          {isLoading ? (
            <ul className="space-y-2" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => (
                <li
                  key={i}
                  className="border-border flex items-center gap-3 rounded-md border p-3"
                >
                  <Skeleton className="h-4 w-4 rounded-sm" />
                  <Skeleton className="h-10 w-10 rounded-md" />
                  <Skeleton className="h-4 w-40" />
                </li>
              ))}
            </ul>
          ) : error ? (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-400"
            >
              We couldn&apos;t load your order history. Please try again.
            </div>
          ) : candidates.length === 0 ? (
            <div
              className="flex min-h-[200px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 p-6 text-center dark:border-gray-700"
              role="status"
            >
              <PackageSearch
                className="mb-3 h-9 w-9 text-gray-400"
                aria-hidden="true"
              />
              <p className="font-medium text-gray-900 dark:text-white">
                Nothing to add yet
              </p>
              <p className="mt-1 max-w-xs text-sm text-gray-500 dark:text-gray-400">
                Products you&apos;ve ordered will show up here so you can start
                tracking them. You&apos;re already tracking everything else.
              </p>
            </div>
          ) : (
            <>
              <label className="border-border flex cursor-pointer items-center gap-2 border-b px-1 pb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  aria-label="Select all products"
                />
                Select all ({candidates.length})
              </label>
              <ul className="max-h-[40vh] space-y-1 overflow-y-auto pt-2">
                {candidates.map((candidate) => {
                  const title = stockItemTitle(candidate);
                  const variantLabel = stockItemVariantLabel(candidate);
                  const sku = candidate.variant?.sku ?? null;
                  const trackLabel = variantLabel
                    ? `${title} (${variantLabel})`
                    : title;
                  const thumbnail =
                    candidate.variant?.product?.thumbnail ?? null;
                  const checked = selected.has(candidate.variant_id);
                  return (
                    <li key={candidate.variant_id}>
                      <label className="flex cursor-pointer items-center gap-3 rounded-md p-2 transition-colors hover:bg-gray-50 dark:hover:bg-neutral-800/60">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggle(candidate.variant_id)}
                          aria-label={`Track ${trackLabel}`}
                        />
                        <div className="border-border flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border bg-gray-50 dark:bg-neutral-900">
                          <ProductThumbnailImage
                            src={thumbnail}
                            alt=""
                            className="h-full w-full object-cover"
                            fallback={
                              <ImageOff
                                className="h-4 w-4 text-gray-300 dark:text-gray-600"
                                aria-hidden="true"
                              />
                            }
                          />
                        </div>
                        <div className="min-w-0">
                          <TruncateTooltip
                            as="p"
                            className="text-sm font-medium text-gray-900 dark:text-white"
                          >
                            {title}
                          </TruncateTooltip>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500 dark:text-gray-400">
                            {variantLabel ? (
                              <span className="border-border inline-flex items-center rounded border px-1.5 py-0.5 font-medium text-gray-700 dark:text-gray-300">
                                {variantLabel}
                              </span>
                            ) : null}
                            {sku ? <span>SKU {sku}</span> : null}
                          </div>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
              {truncated ? (
                <p className="px-1 pt-2 text-xs text-gray-400">
                  Showing the {candidates.length} most recent of {total}. Track
                  these first, then reopen to add more.
                </p>
              ) : null}
            </>
          )}
        </div>

        <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <label
              htmlFor="seed-reorder-level"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Reorder at
            </label>
            <Input
              id="seed-reorder-level"
              type="number"
              inputMode="numeric"
              min={0}
              value={level}
              onChange={(event) => setLevel(event.target.value)}
              className="h-9 w-20"
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={bulk.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={selected.size === 0 || bulk.isPending}
            >
              {bulk.isPending
                ? "Adding…"
                : selected.size > 0
                  ? `Track ${selected.size} ${selected.size === 1 ? "item" : "items"}`
                  : "Track items"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
